import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Session, Message } from 'talkjs/all';
import { TF911Conversation } from './textFrom911.typings';
import { TextFrom911State } from './textFrom911Slice';
import { setConversationUnread } from './textFrom911Slice';

type StateWithTextFrom911 = {
  textFrom911: TextFrom911State;
};

const useHandleTF911UnreadMessages = (session: Session | null) => {
  const dispatch = useDispatch();
  const conversations = useSelector(
    (state: StateWithTextFrom911) => state.textFrom911?.conversations,
  );

  const listenerSetup = useRef(false);
  const conversationsRef =
    useRef<Record<string, TF911Conversation>>(conversations);

  // Keep ref in sync so the onMessage closure always reads fresh state
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  // onMessage() listeners stack, not replace — ensure we only register once per session
  useEffect(() => {
    if (!session?.onMessage || listenerSetup.current) {
      return;
    }

    listenerSetup.current = true;

    session.onMessage((message: Message) => {
      const conversationID = message.conversation.id;

      if (!conversationID.startsWith('tf911.')) {
        return;
      }

      const current = conversationsRef.current;
      const messageCallerID = Object.keys(current).find(
        callerID => current[callerID]?.data?.conversation_id === conversationID,
      );

      if (!messageCallerID) {
        return;
      }

      dispatch(
        setConversationUnread({
          callerID: messageCallerID,
          messageID: message.id,
          hasUnreadMessages: true,
        }),
      );
    });

    return () => {
      listenerSetup.current = false;
    };
  }, [dispatch, session]);
};

export default useHandleTF911UnreadMessages;
